// Library — port of Ryan's MagicPatterns ShelfScreen mechanics (vanilla,
// no framer-motion). Wrapping infinite carousel, swipe-to-step, tap active
// cover to open, tap side cover to arm it, meta (character + pull quote +
// "Tap cover to enter") travels with the active book.
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getImportedProject } from "../api/client";
import { useArc } from "../state/arc";
import Book, { leatherFor } from "../components/Book";

interface ShelfEntry {
  projectId:   string;
  scriptName:  string;
  character:   string | null;
  byline?:     string;
  pullQuote?:  string;
  lastVisited: number;
}

const LOCAL_KEY = "offbook.shelf.v1";

const DEMO_ENTRY: ShelfEntry = {
  projectId:   "79702fff8b05",
  scriptName:  "Three Sisters",
  character:   "VERSHININ",
  byline:      "by Anton Chekhov",
  pullQuote:   "If only one knew, if only one knew.",
  lastVisited: 0,
};

function loadShelf(): ShelfEntry[] {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

export function saveShelfEntry(entry: ShelfEntry) {
  const cur = loadShelf();
  const filtered = cur.filter((e) => e.projectId !== entry.projectId);
  const next = [{ ...entry, lastVisited: Date.now() }, ...filtered].slice(0, 24);
  try { localStorage.setItem(LOCAL_KEY, JSON.stringify(next)); } catch { /* no-op */ }
}

// Shortest signed distance on the ring (infinite-loop helper).
function wrappedOffset(i: number, active: number, n: number): number {
  let offset = i - active;
  if (offset > n / 2) offset -= n;
  if (offset < -n / 2) offset += n;
  return offset;
}

const SPACING = 184;
const SWIPE_THRESHOLD = 40;

export default function Library() {
  const navigate = useNavigate();
  const { update } = useArc();
  const [shelf, setShelf] = useState<ShelfEntry[]>([]);
  const [active, setActive] = useState(0);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const touchStartX = useRef<number | null>(null);
  const touchStartT = useRef<number>(0);

  useEffect(() => {
    const local = loadShelf();
    const hasDemo = local.some((e) => e.projectId === DEMO_ENTRY.projectId);
    setShelf(hasDemo ? local : [DEMO_ENTRY, ...local]);
  }, []);

  const N = shelf.length;
  const activeEntry = shelf[active];

  async function openProject(entry: ShelfEntry) {
    setLoadingId(entry.projectId);
    try {
      const env: any = await getImportedProject(entry.projectId);
      update({
        parsedProject: env.project,
        parsedScript:  env.parsed_script ?? null,
        projectId:     env.project_id ?? entry.projectId,
        methodologyId: "chubbuck",
        selectedCharacter: entry.character ?? null,
      });
      navigate(entry.character ? "/script" : "/character");
    } catch (e: any) {
      alert(`Couldn't load — ${e?.message ?? "unknown"}`);
    } finally {
      setLoadingId(null);
    }
  }

  function stepTo(dir: number) {
    if (N === 0) return;
    setActive((a) => (a + dir + N) % N);
  }

  function onTouchStart(e: React.TouchEvent) {
    touchStartX.current = e.touches[0].clientX;
    touchStartT.current = performance.now();
  }
  function onTouchEnd(e: React.TouchEvent) {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dt = performance.now() - touchStartT.current;
    const v = Math.abs(dx) / Math.max(1, dt) * 1000;
    if (dx < -SWIPE_THRESHOLD || (dx < -10 && v > 300)) stepTo(1);
    else if (dx > SWIPE_THRESHOLD || (dx > 10 && v > 300)) stepTo(-1);
    touchStartX.current = null;
  }

  function onCoverTap(i: number) {
    const off = wrappedOffset(i, active, N);
    if (off === 0) {
      if (activeEntry) openProject(activeEntry);
    } else {
      stepTo(off > 0 ? 1 : -1);
    }
  }

  return (
    <div className="v2" style={{ position: "relative", minHeight: "80vh" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0 0.4rem 0.6rem" }}>
        <span className="v2-tracked-caps" style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <span style={{ display: "inline-block", width: "6px", height: "6px", borderRadius: "50%", background: "var(--v2-gold)" }} />
          Library
        </span>
        <button
          className="v2-affordance"
          style={{ fontSize: "1.3rem", letterSpacing: 0, padding: "0.2rem 0.5rem" }}
          onClick={() => navigate("/upload")}
          aria-label="Bring a script"
        >
          +
        </button>
      </div>

      {N === 0 ? (
        <>
          <p className="v2-prompt" style={{ textAlign: "center", marginTop: "2rem" }}>
            Nothing on the shelf yet.
          </p>
          <div style={{ display: "flex", justifyContent: "center", marginTop: "1.5rem" }}>
            <button className="v2-affordance" onClick={() => navigate("/upload")}>Bring a script</button>
          </div>
        </>
      ) : (
        <>
          {/* Cover carousel — active book sits dead-center */}
          <div
            style={{
              position: "relative", width: "100%", height: "320px",
              overflow: "hidden", touchAction: "pan-y",
            }}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            {shelf.map((entry, i) => {
              const offset = wrappedOffset(i, active, N);
              const isActive = offset === 0;
              if (Math.abs(offset) > 1) return null; // off-stage
              return (
                <div
                  key={entry.projectId}
                  onClick={() => onCoverTap(i)}
                  style={{
                    position: "absolute",
                    top: "50%", left: "50%",
                    transform: `translate(calc(-50% + ${offset * SPACING}px), -50%) scale(${isActive ? 1 : 0.86})`,
                    opacity: isActive ? 1 : 0.55,
                    zIndex: 20 - Math.abs(offset),
                    transition: "transform 360ms cubic-bezier(0.22,1,0.36,1), opacity 320ms ease",
                    cursor: "pointer",
                  }}
                  role="button"
                  aria-label={isActive ? `Open ${entry.scriptName}` : entry.scriptName}
                >
                  <Book
                    title={entry.scriptName}
                    byline={entry.byline ?? (entry.scriptName === DEMO_ENTRY.scriptName ? "by Anton Chekhov" : undefined)}
                    theme={leatherFor(entry.projectId)}
                  />
                </div>
              );
            })}
          </div>

          {/* Meta — character + quote + hint */}
          <div style={{ textAlign: "center", padding: "1rem 1.5rem 0", minHeight: "10rem" }}>
            {activeEntry?.character && (
              <p className="v2-tracked-caps" style={{ marginBottom: "0.7rem", color: "var(--v2-ink-faint)", fontSize: "0.6rem", letterSpacing: "0.32em" }}>
                As {activeEntry.character}
              </p>
            )}
            {activeEntry?.pullQuote && (
              <p style={{
                fontFamily: "var(--v2-serif)", fontStyle: "italic", fontWeight: 300,
                color: "var(--v2-ink-soft)", fontSize: "1.15rem", lineHeight: 1.45,
                maxWidth: "18rem", margin: "0 auto 1.2rem",
              }}>
                "{activeEntry.pullQuote}"
              </p>
            )}
            <p className="v2-tracked-caps" style={{
              fontSize: "0.55rem", letterSpacing: "0.32em",
              color: loadingId === activeEntry?.projectId ? "var(--v2-gold)" : "var(--v2-ink-faint)",
            }}>
              {loadingId === activeEntry?.projectId ? "Opening…" : "Tap cover to enter"}
            </p>
          </div>

          {N > 1 && (
            <div className="v2-shelf-dots" style={{ marginTop: "1.4rem" }}>
              {shelf.map((_, i) => (
                <div key={i} className={"v2-shelf-dot" + (i === active ? " is-active" : "")} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
